import { captureRecapBoundary, getFeedbackPublication } from '@/lib/api';
import { isCurrentDraftContext, type DraftContext } from '@/features/coach-review/composerDrafts';
import { decodeCoachingFeedbackReceipt } from '@/features/draft-inbox/canonicalFeedbackReview';
import type { CoachingFeedbackAcceptedResponse } from '@/lib/types';
import type { SavedPrivateFeedbackParent } from './resolveSavedPrivateFeedback';

export type SavedPublicFeedbackRequest = Readonly<{
  owner: DraftContext;
  accepted: CoachingFeedbackAcceptedResponse;
}>;

/** Explicit Open only. Historical public acceptance neither grants current
 * access nor supplies current CAS. No private fallback, writes or cached read. */
export async function resolvePublishedAssistFeedback(
  input: SavedPublicFeedbackRequest, signal: AbortSignal,
): Promise<SavedPrivateFeedbackParent> {
  const owner = Object.freeze({ ...input.owner }), receipt = decodeCoachingFeedbackReceipt(input.accepted);
  if (!receipt) throw new Error('invalid_public_feedback_receipt');
  const feedbackId = receipt.feedback_id.toLowerCase();
  const boundary = captureRecapBoundary({ ...owner, view: 'coach' });
  const current = () => {
    if (signal.aborted || !boundary.current() || !isCurrentDraftContext(owner))
      throw new DOMException('Saved Feedback context changed', 'AbortError');
  };
  try {
    current();
    const result = await getFeedbackPublication(feedbackId, { scope: owner, signal });
    current();
    if (!result || result.schema_version !== 'feedback-publication.v1'
        || typeof result.feedback_id !== 'string' || result.feedback_id.toLowerCase() !== feedbackId
        || !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(result.feedback_revision_id)
        || !Number.isInteger(result.feedback_version) || result.feedback_version < 1 || result.feedback_version > 2147483647)
      throw new Error('invalid_saved_feedback_parent');
    return Object.freeze({ feedbackId, feedbackVersion: result.feedback_version, publicationState: 'published' });
  } catch (error) {
    current();
    throw error;
  } finally { boundary.dispose(); }
}
